/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import CaseApi from '../case/apis';
import * as actionType from '../store/state/actions/index';
import { ActionManagerInstance } from '../actions/manager';
import * as StateManager from '../store/state/state-manager';
import Constants, { publicConstants } from '../constants';
import EnvironmentInfo from '../environment-info';
import { createC11nEnv } from '../interpreter/c11n-env';
import type { Config } from '../interpreter/types';

import type { CaseOptions } from './types';
/**
 * Provides APIs useful for Mashup use cases.
 */
class MashupApi {
  /**
   * Loads the case by caseId into target mashup
   * @function
   * @public
   *
   * @param caseId to openCase
   * @param targetContext to target different container
   * @param options - object containing extra infomation like pageName
   * @returns promise
   *
   * @example <caption>Example for loadCase api</caption>
   * PCore.getMashupApi().openCase('OPB1HW-SPACE-WORK RA-10001');
   */
  static openCase(caseId: string, targetContext = publicConstants.APP.APP, options: CaseOptions = {}) {
    // setting the renderInline flag true to render the createstage without modal dialog box
    options.renderInline = true;
    // Setting the pageName as pyDetails to render the full page if we haven't received it from MashUp API(Pega-Embed)
    if (!options.pageName) {
      EnvironmentInfo.setDefaultCasePage(Constants.PY_DETAILS);
      options.pageName = Constants.PY_DETAILS;
    } else {
      EnvironmentInfo.setDefaultCasePage(options.pageName);
    }

    if (options?.disableAssignmentHeader) {
      EnvironmentInfo.disableAssignmentHeader(options.disableAssignmentHeader);
    } else {
      EnvironmentInfo.disableAssignmentHeader(false);
    }

    return CaseApi.openCase(caseId, targetContext, Constants.PRIMARY, options);
  }

  /**
   * Creates case and loads view into target mashup
   * @function
   * @public
   *
   * @param className Name of the case class
   * @param targetContext to target different container
   * @param options object containing extra infomation like startingFields and pageName
   * @returns promise
   *
   * @example <caption>Example for createCase api</caption>
   * const options = {
   *  pageName: "pyEmbedAssignment",
   *  startingFields: {
   *    FirstName: "Adam",
   *    LastName: "Smith",
   *    Vehicle: {
   *      Make: "Honda",
   *      Model: "Accord"
   *    }
   *  }
   *}
   * PCore.getMashupApi().createCase('OXJ4P4-CoWin-Work-Feedback', options);
   */
  static createCase(className: string, targetContext = publicConstants.APP.APP, options: CaseOptions = {}) {
    // setting the renderInline flag true to render the createstage without modal dialog box
    options.renderInline = true;
    // Setting the pageName as pyDetails to render the full page if we haven't received it from MashUp API(Pega-Embed)
    if (!options.pageName) {
      EnvironmentInfo.setDefaultCasePage(Constants.PY_DETAILS);
      options.pageName = Constants.PY_DETAILS;
    } else {
      EnvironmentInfo.setDefaultCasePage(options.pageName);
    }

    if (options?.disableAssignmentHeader) {
      EnvironmentInfo.disableAssignmentHeader(options.disableAssignmentHeader);
    } else {
      EnvironmentInfo.disableAssignmentHeader(false);
    }

    return CaseApi.createCase(className, targetContext, Constants.PRIMARY, options);
  }

  /**
   * Obtains the assignment that contains the highest priority and loads it into a target context.
   * @function
   * @public
   *
   * @param targetContext The context where the assignment must be loaded. If the value of targetContext is not provided, the default value is app.
   * @param options The JavaScript object containing the property that can be used to load a specific view. By Default only pageName property is passed
   * @returns promise
   *
   * @example <caption>In this example, the API loads the assignment with the highest priority into the workarea context.</caption>
   * PCore.getMashupApi().getNextWork('workarea', { pageName: 'pyEmbedAssignment' });
   */
  static getNextWork(targetContext = publicConstants.APP.APP, options: CaseOptions = {}) {
    // setting the renderInline flag true to render the createstage without modal dialog box
    options.renderInline = true;
    // Setting the pageName as pyDetails to render the full page if we haven't received it from MashUp API(Pega-Embed)
    if (!options.pageName) {
      EnvironmentInfo.setDefaultCasePage(Constants.PY_DETAILS);
      options.pageName = Constants.PY_DETAILS;
    } else {
      EnvironmentInfo.setDefaultCasePage(options.pageName);
    }

    if (options?.disableAssignmentHeader) {
      EnvironmentInfo.disableAssignmentHeader(options.disableAssignmentHeader);
    } else {
      EnvironmentInfo.disableAssignmentHeader(false);
    }

    return CaseApi.getNextWork(targetContext, Constants.PRIMARY, options);
  }

  /**
   * Loads the assignment into target mashup
   * @function
   * @public
   *
   * @param assignmentId assignment id to load
   * @param targetContext to target different container
   * @param options object containing extra infomation like pageName
   * @returns promise
   *
   * @example <caption>Example for loadCase api</caption>
   * PCore.getMashupApi().openAssignment('Work-Test M-12!Assignment_id');
   */
  static openAssignment(assignmentId: string, targetContext = publicConstants.APP.APP, options: CaseOptions = {}) {
    // setting the renderInline flag true to render the createstage without modal dialog box
    options.renderInline = true;
    // Setting the pageName as pyDetails to render the full page if we haven't received it from MashUp API(Pega-Embed)
    if (!options.pageName) {
      EnvironmentInfo.setDefaultCasePage(Constants.PY_DETAILS);
      options.pageName = Constants.PY_DETAILS;
    } else {
      EnvironmentInfo.setDefaultCasePage(options.pageName);
    }

    if (options?.disableAssignmentHeader) {
      EnvironmentInfo.disableAssignmentHeader(options.disableAssignmentHeader);
    } else {
      EnvironmentInfo.disableAssignmentHeader(false);
    }

    return CaseApi.openAssignment(assignmentId, targetContext, Constants.PRIMARY, options);
  }

  /**
   * Opens the page view into the target mashup
   * @function
   * @public
   * @param pageName Name of the page
   * @param className Name of the page class
   * @param targetContext to target different container
   * @param options object containing extra infomation like defaultCasePage
   * @returns promise
   *
   * @example <caption>Example for openPage api</caption>
   * PCore.getMashupApi().openCase('pyHome','Data-Portal');
   */
  static openPage(
    pageName: string,
    className: string,
    targetContext = publicConstants.APP.APP,
    options: CaseOptions = {}
  ): Promise<unknown> {
    const theType = actionType.SHOW_PAGE;
    if (options?.defaultCasePage) {
      EnvironmentInfo.setDefaultCasePage(options.defaultCasePage);
    } else {
      EnvironmentInfo.setDefaultCasePage(Constants.PY_DETAILS);
    }

    if (options?.disableAssignmentHeader) {
      EnvironmentInfo.disableAssignmentHeader(options.disableAssignmentHeader);
    } else {
      EnvironmentInfo.disableAssignmentHeader(false);
    }
    const actionInfo = {
      viewName: pageName,
      className,
      acTargetName: Constants.PRIMARY,
      actionMgrID: ActionManagerInstance.getNextActionMgrId(),
      context: targetContext
    };

    const { promise } = ActionManagerInstance.actionManagerPromise(theType, actionInfo.actionMgrID);

    StateManager.dispatch({
      type: theType,
      payload: { ...actionInfo }
    });
    return promise;
  }

  /**
     * Returns context API
     * @function
     * @public
     *
     * @param pageReference Page reference
     * @param targetContext to target different container

     * @returns This API returns the PConnect object which provides access to context APIs
     *
     * @example <caption>Example for using Context APIs</caption>
     * const contextAPI = PCore.getMashupApi().getCurrentContextAPI("MashupPage");
     * contextAPI.setValue(".BannerLabel", "Welcome!");
     *
     * const contextAPI = PCore.getMashupApi().getCurrentContextAPI("caseInfo.content", "app/primary_1/workarea_2");
     * contextAPI.setValue(".CustomerName", "Connor");
     */
  static getCurrentContextAPI(pageReference: string, targetContext = publicConstants.APP.APP) {
    const config: Config = {
      meta: {
        config: {}
      },
      options: {
        context: targetContext,
        pageReference,
        containerName: Constants.PRIMARY
      }
    };
    return createC11nEnv(config).getPConnect();
  }
}

export default MashupApi;
